import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControlLabel,
  Grid,
  Snackbar,
  TextField,
  Typography,
} from "@mui/material";
import { Table, Tag, Tooltip } from "antd";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import RefreshOutlinedIcon from "@mui/icons-material/RefreshOutlined";
import ListAltOutlinedIcon from "@mui/icons-material/ListAltOutlined";
import CancelOutlinedIcon from "@mui/icons-material/CancelOutlined";
import { Link, Navigate } from "react-router-dom";
import { useSelector } from "react-redux";
import NewMiniDrawer from "../../components/navMenu/menu-nav";
import DialogFilaTransport from "../../components/dialog-fila-transport/dialog-fila-transport";
import CancelFreightDialog from "../frete-copy/components/CancelFreightDialog";
import {
  cancelFreightById,
  canCancelFreightStatus,
  getCancelFreightErrorMessage,
  resolveFreightStatusCode,
} from "../../util/freight/cancelFreight";
import {
  acceptTransportQueueHubCandidate,
  fetchTransportQueueHubSummary,
} from "../../service/transportQueueHub.api";
import { getAcceptTransportErrorMessage } from "../../components/notifications/acceptTransportFromQueue";
import {
  buildHubQueryParams,
  EMPTY_QUEUE_HUB_FILTERS,
  formatCurrency,
  formatDateBr,
  formatOldestWaiting,
  formatWaitingTime,
  mapTransportQueueHubFreights,
} from "./transportQueueHub.mapper";
import "./filas-transportadoras.css";

const acceptButtonSx = { bgcolor: "#012442", "&:hover": { bgcolor: "#01365f" } };
const cancelButtonSx = {
  color: "#ff3100",
  borderColor: "rgba(255, 49, 0, 0.45)",
  "&:hover": {
    borderColor: "#ff3100",
    backgroundColor: "rgba(255, 49, 0, 0.06)",
  },
};

function canShowCancelForFreight(freight, user) {
  const canEdit = Boolean(user?.permissoes?.freight?.isEdit || user?.viewALL);
  if (!canEdit) return false;

  const statusCode = resolveFreightStatusCode(freight?.status);
  return statusCode !== "08" && canCancelFreightStatus(statusCode);
}

export default function FilasTransportadorasPage() {
  const isLoggedIn = useSelector((state) => state.usuarioLogado) > 0;
  const user = useSelector((state) => state.user);

  const [filters, setFilters] = useState({ ...EMPTY_QUEUE_HUB_FILTERS });
  const [appliedFilters, setAppliedFilters] = useState({ ...EMPTY_QUEUE_HUB_FILTERS });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [summary, setSummary] = useState(null);
  const [freightGroups, setFreightGroups] = useState([]);
  const [acceptingKey, setAcceptingKey] = useState(null);
  const [confirmAccept, setConfirmAccept] = useState(null);
  const [transportModalOpen, setTransportModalOpen] = useState(false);
  const [transportModalFreightId, setTransportModalFreightId] = useState(null);
  const [transportModalFreight, setTransportModalFreight] = useState(null);
  const [transportsInFila, setTransportsInFila] = useState([]);
  const [acceptingTransportId, setAcceptingTransportId] = useState(null);
  const [cancelFreightLoading, setCancelFreightLoading] = useState(false);
  const [openCancelFreightDialog, setOpenCancelFreightDialog] = useState(false);
  const [pendingCancelFreight, setPendingCancelFreight] = useState(null);
  const [actionMessage, setActionMessage] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, severity: "info", message: "" });

  const canView =
    user?.permissoes?.filasTransportadoras?.isView ||
    user?.viewALL;

  const loadHub = useCallback(async () => {
    setLoading(true);
    setError("");

    try {
      const params = buildHubQueryParams(appliedFilters, user);
      const data = await fetchTransportQueueHubSummary(params);
      setSummary(data.summary ?? null);
      setFreightGroups(mapTransportQueueHubFreights(data));
    } catch (err) {
      console.error("[FilasTransportadoras] Erro ao carregar central:", err);
      setError("Não foi possível carregar a central de filas.");
      setSummary(null);
      setFreightGroups([]);
    } finally {
      setLoading(false);
    }
  }, [appliedFilters, user]);

  useEffect(() => {
    if (isLoggedIn && canView) {
      loadHub();
    }
  }, [isLoggedIn, canView, loadHub]);

  const handleSearch = () => {
    setAppliedFilters({ ...filters });
  };

  const handleClear = () => {
    setFilters({ ...EMPTY_QUEUE_HUB_FILTERS });
    setAppliedFilters({ ...EMPTY_QUEUE_HUB_FILTERS });
  };

  const refreshAfterAccept = useCallback(async () => {
    await loadHub();
  }, [loadHub]);

  const handleAcceptCandidate = (freight, candidate) => {
    setConfirmAccept({
      ...freight,
      ...candidate,
      rowKey: candidate.rowKey,
    });
  };

  const handleConfirmAccept = async () => {
    if (!confirmAccept || acceptingKey) return;

    setAcceptingKey(confirmAccept.rowKey);

    try {
      const result = await acceptTransportQueueHubCandidate({
        freightId: confirmAccept.freightId,
        idCarrier: confirmAccept.idCarrier,
        nameCarrier: confirmAccept.nameCarrier,
        idUser: user?.uid,
      });

      setSnackbar({
        open: true,
        severity: "success",
        message: result?.message || "Transportadora aceita com sucesso.",
      });
      setConfirmAccept(null);
      await refreshAfterAccept();
    } catch (err) {
      setSnackbar({
        open: true,
        severity: "error",
        message: getAcceptTransportErrorMessage(
          err,
          "Não foi possível aceitar a transportadora.",
        ),
      });
    } finally {
      setAcceptingKey(null);
    }
  };

  const openFreightQueueModal = (freight) => {
    if (!freight?.queue?.length) return;

    setTransportModalFreightId(freight.freightId);
    setTransportModalFreight(freight);
    setTransportsInFila(
      freight.queue.map((candidate) => ({
        idTransport: candidate.idCarrier,
        name: candidate.nameCarrier,
        cnpj: candidate.cnpj,
        registeredAt: candidate.registeredAt,
      })),
    );
    setActionMessage(null);
    setTransportModalOpen(true);
  };

  const handleOpenCancelFreightDialog = (freight) => {
    setPendingCancelFreight(freight);
    setOpenCancelFreightDialog(true);
  };

  const handleCloseCancelFreightDialog = () => {
    if (cancelFreightLoading) return;
    setOpenCancelFreightDialog(false);
    setPendingCancelFreight(null);
  };

  const executeCancelFreight = async (freightId, reason) => {
    await cancelFreightById(freightId, reason, {
      idUser: user?.uid,
      viewAll: Boolean(user?.viewALL),
    });

    setSnackbar({
      open: true,
      severity: "success",
      message: "Frete cancelado com sucesso.",
    });

    if (transportModalFreightId === freightId) {
      setTransportModalOpen(false);
      setTransportModalFreightId(null);
      setTransportModalFreight(null);
      setTransportsInFila([]);
    }

    await loadHub();
  };

  const handleConfirmCancelFreight = async (reason) => {
    const freightId = pendingCancelFreight?.freightId;
    if (!freightId || cancelFreightLoading) return;

    setCancelFreightLoading(true);

    try {
      await executeCancelFreight(freightId, reason);
      handleCloseCancelFreightDialog();
    } catch (err) {
      setSnackbar({
        open: true,
        severity: "error",
        message: getCancelFreightErrorMessage(
          err,
          "Não foi possível cancelar o frete.",
        ),
      });
    } finally {
      setCancelFreightLoading(false);
    }
  };

  const handleCancelFreightFromQueue = async (reason) => {
    if (!transportModalFreightId || cancelFreightLoading) return;

    setCancelFreightLoading(true);

    try {
      await executeCancelFreight(transportModalFreightId, reason);
    } catch (err) {
      setSnackbar({
        open: true,
        severity: "error",
        message: getCancelFreightErrorMessage(
          err,
          "Não foi possível cancelar o frete.",
        ),
      });
    } finally {
      setCancelFreightLoading(false);
    }
  };

  const handleAcceptFromModal = async (transport) => {
    if (!transportModalFreightId || !transport?.idTransport || acceptingTransportId) {
      return;
    }

    setAcceptingTransportId(transport.idTransport);
    setActionMessage(null);

    try {
      const result = await acceptTransportQueueHubCandidate({
        freightId: transportModalFreightId,
        idCarrier: transport.idTransport,
        nameCarrier: transport.name,
        idUser: user?.uid,
      });

      setActionMessage({
        severity: "success",
        text: result?.message || "Transportadora aceita com sucesso.",
      });
      setTransportModalOpen(false);
      await refreshAfterAccept();
    } catch (err) {
      setActionMessage({
        severity: "error",
        text: getAcceptTransportErrorMessage(
          err,
          "Não foi possível aceitar a transportadora.",
        ),
      });
    } finally {
      setAcceptingTransportId(null);
    }
  };

  const buildCandidateColumns = useCallback(
    (freight) => [
      {
        title: "Transportadora",
        key: "nameCarrier",
        render: (_, candidate) => (
          <Box>
            <Typography sx={{ fontWeight: 600 }}>{candidate.nameCarrier}</Typography>
            {candidate.cnpj ? (
              <Typography variant="caption" color="text.secondary">
                CNPJ: {candidate.cnpj}
              </Typography>
            ) : null}
          </Box>
        ),
      },
      {
        title: "Na fila",
        dataIndex: "registeredAt",
        key: "registeredAt",
        width: 160,
        render: (value) => <Tag color="blue">{formatWaitingTime(value)}</Tag>,
      },
      {
        title: "Ações",
        key: "actions",
        align: "right",
        width: 220,
        render: (_, candidate) => {
          const isAccepting = acceptingKey === candidate.rowKey;
          return (
            <Box className="filas-hub-actions">
              <Tooltip title="Visualizar cadastro">
                <Button
                  component={Link}
                  to={`/newEmbarcador/${candidate.idCarrier}`}
                  state={{ viewOnly: true }}
                  size="small"
                  variant="outlined"
                  startIcon={<VisibilityOutlinedIcon />}
                >
                  Ver
                </Button>
              </Tooltip>
              <Button
                size="small"
                variant="contained"
                disabled={isAccepting}
                startIcon={
                  isAccepting ? (
                    <CircularProgress size={14} color="inherit" />
                  ) : (
                    <CheckCircleOutlineIcon />
                  )
                }
                onClick={() => handleAcceptCandidate(freight, candidate)}
                sx={acceptButtonSx}
              >
                Aceitar
              </Button>
            </Box>
          );
        },
      },
    ],
    [acceptingKey],
  );

  const freightColumns = useMemo(
    () => [
      {
        title: "Frete",
        dataIndex: "numberSerial",
        key: "numberSerial",
        render: (value, record) => (
          <Box>
            <Typography className="filas-hub-freight-number">#{value}</Typography>
            <Typography variant="caption" color="text.secondary">
              {record.shipperName}
            </Typography>
          </Box>
        ),
      },
      {
        title: "Rota",
        key: "route",
        render: (_, record) => (
          <Typography className="filas-hub-route" variant="body2">
            {record.origin} → {record.destination}
          </Typography>
        ),
      },
      {
        title: "Coleta",
        dataIndex: "collectionDate",
        key: "collectionDate",
        width: 120,
        render: (value) => formatDateBr(value),
      },
      {
        title: "Valor",
        dataIndex: "freightValue",
        key: "freightValue",
        width: 130,
        render: (value) => formatCurrency(value),
      },
      {
        title: "Candidatos",
        dataIndex: "queueCount",
        key: "queueCount",
        width: 120,
        render: (value, record) => (
          <Box>
            <Tag color="processing" className="filas-hub-queue-count">
              {value} na fila
            </Tag>
            {record.oldestRegisteredAt ? (
              <Typography variant="caption" display="block" color="text.secondary">
                há mais: {formatWaitingTime(record.oldestRegisteredAt)}
              </Typography>
            ) : null}
          </Box>
        ),
      },
      {
        title: "Ações",
        key: "actions",
        align: "right",
        width: 320,
        render: (_, record) => {
          const showCancel = canShowCancelForFreight(record, user);
          const isCancelingThisFreight =
            cancelFreightLoading &&
            pendingCancelFreight?.freightId === record.freightId;

          return (
            <Box className="filas-hub-freight-actions">
              <Button
                size="small"
                variant="outlined"
                component={Link}
                to={`/freightCopy/${record.freightId}`}
              >
                Ver frete
              </Button>
              <Button
                size="small"
                variant="contained"
                startIcon={<ListAltOutlinedIcon />}
                onClick={() => openFreightQueueModal(record)}
                sx={acceptButtonSx}
              >
                Fila
              </Button>
              {showCancel ? (
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={
                    isCancelingThisFreight ? (
                      <CircularProgress size={14} color="inherit" />
                    ) : (
                      <CancelOutlinedIcon />
                    )
                  }
                  disabled={cancelFreightLoading}
                  onClick={() => handleOpenCancelFreightDialog(record)}
                  sx={cancelButtonSx}
                >
                  Cancelar frete
                </Button>
              ) : null}
            </Box>
          );
        },
      },
    ],
    [cancelFreightLoading, pendingCancelFreight, user],
  );

  if (!isLoggedIn) {
    return <Navigate to="/login" />;
  }

  if (!canView) {
    return <Navigate to="/" />;
  }

  return (
    <NewMiniDrawer
      divOpen={
        <Box className="filas-hub-page">
          <Box className="filas-hub-page__header">
            <Typography variant="h5" className="filas-hub-page__title">
              Central de Filas de Transportadoras
            </Typography>
            <Typography variant="body2" className="filas-hub-page__subtitle">
              Fretes agrupados por número — expanda para ver e aceitar transportadoras
              na fila.
            </Typography>
          </Box>

          <Box className="filas-hub-summary">
            <Box className="filas-hub-summary__card">
              <Typography className="filas-hub-summary__label">
                Fretes com fila
              </Typography>
              <Typography className="filas-hub-summary__value">
                {summary?.freightsWithQueue ?? 0}
              </Typography>
            </Box>
            <Box className="filas-hub-summary__card">
              <Typography className="filas-hub-summary__label">
                Candidatos
              </Typography>
              <Typography className="filas-hub-summary__value">
                {summary?.totalCandidates ?? 0}
              </Typography>
            </Box>
            <Box className="filas-hub-summary__card">
              <Typography className="filas-hub-summary__label">
                Aguardando há mais
              </Typography>
              <Typography className="filas-hub-summary__value">
                {formatOldestWaiting(summary?.oldestWaitingMinutes)}
              </Typography>
            </Box>
          </Box>

          <Box className="filas-hub-filters">
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  fullWidth
                  size="small"
                  label="Nº do frete"
                  value={filters.number}
                  onChange={(e) =>
                    setFilters((prev) => ({ ...prev, number: e.target.value }))
                  }
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  fullWidth
                  size="small"
                  label="Origem"
                  value={filters.origin}
                  onChange={(e) =>
                    setFilters((prev) => ({ ...prev, origin: e.target.value }))
                  }
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  fullWidth
                  size="small"
                  label="Destino"
                  value={filters.destination}
                  onChange={(e) =>
                    setFilters((prev) => ({
                      ...prev,
                      destination: e.target.value,
                    }))
                  }
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  fullWidth
                  size="small"
                  label="Embarcador"
                  value={filters.shipperName}
                  onChange={(e) =>
                    setFilters((prev) => ({
                      ...prev,
                      shipperName: e.target.value,
                    }))
                  }
                />
              </Grid>
              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={filters.onlyWithQueue !== false}
                      onChange={(e) =>
                        setFilters((prev) => ({
                          ...prev,
                          onlyWithQueue: e.target.checked,
                        }))
                      }
                    />
                  }
                  label="Mostrar apenas fretes com transportadora na fila"
                />
              </Grid>
            </Grid>
            <Box className="filas-hub-filters__actions">
              <Button variant="text" onClick={handleClear} disabled={loading}>
                Limpar
              </Button>
              <Button
                variant="outlined"
                startIcon={<RefreshOutlinedIcon />}
                onClick={loadHub}
                disabled={loading}
              >
                Atualizar
              </Button>
              <Button
                variant="contained"
                onClick={handleSearch}
                disabled={loading}
                sx={acceptButtonSx}
              >
                Buscar
              </Button>
            </Box>
          </Box>

          {error ? (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          ) : null}

          <Box className="filas-hub-table-wrap">
            <Table
              rowKey="freightId"
              columns={freightColumns}
              dataSource={freightGroups}
              loading={loading}
              pagination={{ pageSize: 10, showSizeChanger: true }}
              locale={{
                emptyText: (
                  <Box className="filas-hub-empty">
                    Nenhum frete com transportadora na fila para os filtros aplicados.
                  </Box>
                ),
              }}
              scroll={{ x: 960 }}
              expandable={{
                defaultExpandAllRows: true,
                rowExpandable: (record) => record.queueCount > 0,
                expandedRowRender: (freight) => (
                  <Box className="filas-hub-nested-wrap">
                    <Table
                      rowKey="rowKey"
                      columns={buildCandidateColumns(freight)}
                      dataSource={freight.queue}
                      pagination={false}
                      size="small"
                    />
                  </Box>
                ),
              }}
            />
          </Box>

          <Dialog
            open={Boolean(confirmAccept)}
            onClose={() => !acceptingKey && setConfirmAccept(null)}
            maxWidth="sm"
            fullWidth
          >
            <DialogTitle>Confirmar aceite da transportadora</DialogTitle>
            <DialogContent>
              <DialogContentText component="div">
                <Typography variant="body2" sx={{ mb: 1 }}>
                  Atribuir o frete <strong>#{confirmAccept?.numberSerial}</strong>{" "}
                  ({confirmAccept?.origin} → {confirmAccept?.destination}) à
                  transportadora{" "}
                  <strong>{confirmAccept?.nameCarrier}</strong>?
                </Typography>
              </DialogContentText>
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2 }}>
              <Button
                onClick={() => setConfirmAccept(null)}
                disabled={Boolean(acceptingKey)}
              >
                Cancelar
              </Button>
              <Button
                variant="contained"
                onClick={handleConfirmAccept}
                disabled={Boolean(acceptingKey)}
                sx={acceptButtonSx}
              >
                {acceptingKey ? "Aceitando..." : "Confirmar aceite"}
              </Button>
            </DialogActions>
          </Dialog>

          <DialogFilaTransport
            transportsInFila={transportsInFila}
            openModal={transportModalOpen}
            handleClose={() => {
              setTransportModalOpen(false);
              setTransportModalFreightId(null);
              setTransportModalFreight(null);
              setTransportsInFila([]);
              setAcceptingTransportId(null);
              setActionMessage(null);
              setCancelFreightLoading(false);
            }}
            acceptingTransportId={acceptingTransportId}
            onAcceptTransport={handleAcceptFromModal}
            canAccept
            actionMessage={actionMessage}
            freightId={transportModalFreightId}
            freightStatus={transportModalFreight?.status}
            canCancelFreight={
              Boolean(user?.permissoes?.freight?.isEdit || user?.viewALL)
            }
            onCancelFreight={handleCancelFreightFromQueue}
            cancelLoading={cancelFreightLoading}
          />

          <CancelFreightDialog
            open={openCancelFreightDialog}
            loading={cancelFreightLoading}
            onClose={handleCloseCancelFreightDialog}
            onConfirm={handleConfirmCancelFreight}
            message="O frete será cancelado e sairá da lista de disponíveis. Transportadoras na fila serão notificadas."
          />

          <Snackbar
            open={snackbar.open}
            autoHideDuration={5000}
            onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
            anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
          >
            <Alert
              severity={snackbar.severity}
              onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
            >
              {snackbar.message}
            </Alert>
          </Snackbar>
        </Box>
      }
    />
  );
}
